import React, {useEffect, useMemo, useState} from 'react';
import PropTypes from 'prop-types';
import {Box, Grid, LinearProgress, Typography} from '@mui/material';
import {FormProvider, useForm} from 'react-hook-form';
import * as allConstants from '../../../constants/allConstants';
import {inputTypes} from '../../../pieces/inputs/formInput';
import CustomFormBlock from '../../../pieces/inputs/formBlock';
import FormSubmitButtons from '../../../pieces/formSubmitButtons';
import ApiUtils from '../../../utils/apiUtils';
import {unwrapRequestResult} from '../../../utils/unwrapRequestResult';

export const SetCompanyAdminForm = (props) => {
  const {COMPANY_FIELDS} = allConstants;
  const [companyAdminOptions, setCompanyAdminOptions] = useState([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);

  const getDefaultAdminUserId = () => {
    if(typeof props.adminUserId === 'string') {
      return props.adminUserId;
    }
    return props.adminUserId?.id || '';
  };

  const formMethods = useForm({
    mode: 'onBlur',
    defaultValues: {
      [COMPANY_FIELDS.ADMIN_USER_ID.api_name]: getDefaultAdminUserId(),
    },
  });

  useEffect(() => {
    const fetchCompanyUsers = async () => {
      if(!props.companyId) {
        setCompanyAdminOptions([]);
        return;
      }

      setIsLoadingUsers(true);
      const response = await ApiUtils.makeApiCall('GET', `companies/get-users-by-company-id/${props.companyId}`);
      if(response?.status === 'success') {
        const options = (response?.data?.users || []).map((user) => {
          const fullName = `${user?.firstName || ''} ${user?.lastName || ''}`.trim();
          const withEmail = user?.email ? `${fullName || user.email} (${user.email})` : fullName;
          return {
            name: withEmail || user?._id,
            value: user?._id,
          };
        });
        setCompanyAdminOptions(options);
      }
      setIsLoadingUsers(false);
    };

    fetchCompanyUsers();
  }, [props.companyId]);

  useEffect(() => {
    formMethods.reset({
      [COMPANY_FIELDS.ADMIN_USER_ID.api_name]: getDefaultAdminUserId(),
    });
  }, [props.adminUserId, formMethods]);

  const inputDefaultProps = useMemo(() => ({
    viewMode: false,
    isEditable: true,
    renderLabel: true,
    nested: false,
    parent: null,
  }), []);

  const hasCompanyUsers = companyAdminOptions.length > 0;
  const shouldDisableDropdown = isLoadingUsers || !hasCompanyUsers;

  const onSubmit = async (data) => {
    let adminUserId = data?.[COMPANY_FIELDS.ADMIN_USER_ID.api_name];
    if(adminUserId && typeof adminUserId === 'object') {
      adminUserId = adminUserId.value || adminUserId.id || '';
    }

    const responseStatus = await ApiUtils.makeApiCall(
      'POST',
      'companies/set-company-admin',
      JSON.stringify({
        companyId: props.companyId,
        adminUserId: adminUserId || '',
      })
    );

    const unwrapped = unwrapRequestResult({
      showSuccess: true,
      result: responseStatus,
      successMessage: 'Company admin successfully updated.',
      id: 'set_company_admin',
    });

    if(unwrapped?.status !== 'error') {
      props.onSuccess();
    }
  };

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={formMethods.handleSubmit(onSubmit)}>
        <Grid container spacing={2} sx={{minWidth: '300px', maxWidth: '360px', p: 1}}>
          <Grid item xs={12}>
            {isLoadingUsers && <LinearProgress/>}
          </Grid>
          <Grid item xs={12}>
            <Typography>Select company admin user</Typography>
          </Grid>
          <Grid item xs={12}>
            <CustomFormBlock
              {...inputDefaultProps}
              isEditable={!shouldDisableDropdown}
              field={COMPANY_FIELDS.ADMIN_USER_ID}
              inputType={inputTypes.singleSelect}
              options={companyAdminOptions}
              defaultValue={getDefaultAdminUserId()}
              fullWidth
            />
            {isLoadingUsers && <Typography variant={'body2'} mt={1}>Loading company users...</Typography>}
            {!isLoadingUsers && !hasCompanyUsers && (
              <Typography variant={'body2'} mt={1} color={'error'}>
                No users found for this company. Please add a company user first.
              </Typography>
            )}
          </Grid>
          <Grid item xs={12}>
            <Box display={'flex'} justifyContent={'center'}>
              <FormSubmitButtons
                cancelAction={props.handleClose}
                cancelText={'Cancel'}
                submitText={'Update'}
                disableSubmit={shouldDisableDropdown}
              />
            </Box>
          </Grid>
        </Grid>
      </form>
    </FormProvider>
  );
};

SetCompanyAdminForm.propTypes = {
  companyId: PropTypes.string,
  adminUserId: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({
      id: PropTypes.string,
      firstName: PropTypes.string,
      lastName: PropTypes.string,
      email: PropTypes.string,
    })
  ]),
  handleClose: PropTypes.func,
  onSuccess: PropTypes.func,
};

SetCompanyAdminForm.defaultProps = {
  companyId: '',
  adminUserId: '',
  handleClose: () => {},
  onSuccess: () => {},
};
